import { css } from '@emotion/react';
import { tokens, applyFont, variables, shadow } from '@domain-group/fe-brary';
import { media } from '../../styles/breakpoints-2026';

const { spacing, colors, typography } = tokens;

const CONTENT_MAX_WIDTH = 1248;
const RAIL_WIDTH = 320;
const RAIL_WIDTH_LARGE = 338;

const SEARCH_FIELD_WIDTH = 580;
const SEARCH_FIELD_MIN_WIDTH = 400;
const SEARCH_FIELD_HEIGHT = 58;
const SEARCH_BAR_PADDING = spacing.s;
const SEARCH_BAR_HEIGHT = `calc(${SEARCH_FIELD_HEIGHT}px + ${SEARCH_BAR_PADDING} * 2 + 1px)`;

const STICKY_TOP = `calc(${SEARCH_BAR_HEIGHT} + ${spacing.l})`;

const ABOVE_PILLS_SCRIM = +variables.global.layerFloating - 1;

// Below desktop, the typeahead sits above the filter pills in the same
// sticky bar. Offsetting `top` by its height keeps only the pills stuck
// to the viewport once the typeahead has scrolled past.
const STACKED_TYPEAHEAD_HEIGHT = `calc(${spacing.s} + ${SEARCH_FIELD_HEIGHT}px + 2px)`;

export const page = css`
  background-color: ${colors.neutral.background.default};
`;

export const wrapper = css`
  box-sizing: border-box;
  width: 100%;
  max-width: ${CONTENT_MAX_WIDTH}px;
  margin: 0 auto;
  padding: 0 ${spacing.m};
`;

export const searchBar = (isSticky: boolean) => css`
  background-color: ${colors.neutral.surface.default};
  border-bottom: 1px solid ${colors.neutral.trim.default};
  position: sticky;
  top: calc(-1 * ${STACKED_TYPEAHEAD_HEIGHT});
  z-index: ${ABOVE_PILLS_SCRIM};
  transition: box-shadow 0.2s ease;

  ${media('>=desktop')} {
    top: 0;
    z-index: ${variables.global.layerPopout};
  }

  ${isSticky &&
  css`
    box-shadow: ${shadow(tokens.shadows.elevation3)};
    /* don't double up on the border while the shadow is showing */
    border-color: ${colors.neutral.surface.default};
  `}
`;

export const searchBarInner = css`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.s};
  padding-top: ${spacing.s};
  padding-bottom: ${spacing.s};

  ${media('>=desktop')} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: ${spacing.m};
  }
`;

export const typeahead = css`
  width: 100%;
  min-height: ${SEARCH_FIELD_HEIGHT}px;

  ${media('>=desktop')} {
    flex: 1 1 ${SEARCH_FIELD_WIDTH}px;
    min-width: ${SEARCH_FIELD_MIN_WIDTH}px;
    max-width: ${SEARCH_FIELD_WIDTH}px;
  }
`;

export const filterGroup = css`
  display: flex;
  align-items: center;
  gap: ${spacing.s};

  ${media('>=desktop')} {
    min-height: ${SEARCH_FIELD_HEIGHT}px;
  }
`;

export const headingSection = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.l};

  ${media('>=desktop')} {
    grid-column: 1;
    grid-row: 1;
  }
`;

export const headingRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.m};
`;

export const heading = css`
  ${applyFont(typography.caption)}
  color: ${colors.neutral.medium.default};
  margin: 0;
  min-width: 0;

  ${media('<tablet')} {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
`;

export const grid = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.l};
  padding-top: ${spacing.m};
  padding-bottom: ${spacing.xl};

  ${media('>=desktop')} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) ${RAIL_WIDTH}px;
    column-gap: ${spacing.xl};
    align-items: start;
  }

  ${media('>lrg-desktop')} {
    grid-template-columns: minmax(0, 1fr) ${RAIL_WIDTH_LARGE}px;
    column-gap: ${spacing.xxl};
  }
`;

export const results = css`
  position: relative;

  ${media('>=desktop')} {
    grid-column: 1;
    grid-row: 2;
  }
`;

export const resultsContent = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.l};
`;

export const dimmed = css`
  opacity: 0.5;
  pointer-events: none;
`;

export const pagination = css`
  display: flex;
  justify-content: center;

  ${media('>=desktop')} {
    grid-column: 1;
    grid-row: 3;
  }
`;

export const breadcrumbBar = css`
  background-color: ${colors.neutral.surface.default};
`;

export const breadcrumbs = css`
  padding-top: ${spacing.xs};
  padding-bottom: ${spacing.xs};

  nav {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    li > a,
    li > span {
      ${applyFont(typography.mini)}
    }
  }
`;

export const rail = css`
  ${media('>=desktop')} {
    grid-column: 2;
    grid-row: 2;
    align-self: stretch;

    > * {
      position: sticky;
      top: ${STICKY_TOP};
    }
  }
`;
